// ═══════════════════════════════════════════════════════════
// API RESPONSE SHAPES — match server contract
// ═══════════════════════════════════════════════════════════

export type ApprovalStatus = 'draft' | 'submitted' | 'approved' | 'rejected';
export type ViewMode = 'daily' | 'weekly';
export type FilterTab = 'all' | 'pending' | 'approved' | 'rejected';
export type TaskStatus = 'ongoing' | 'completed' | 'delayed' | 'yet-to-start';
export type HistoryAction = 'approved' | 'rejected' | 'edited';

export interface ApiEmployee {
  id: string;
  name: string;
  initials: string;
  avatarColor: string;
  role: string;
  pendingDaysCount: number;
  /** From GET manager-timesheets-by-range `submitter` (optional). */
  approvalStatus?: string;
  totalLoggedHours?: number;
  distinctTaskCount?: number;
  /** From GET projects/:id/members-status — e.g. `active` | `inactive`. */
  memberStatus?: string;
  profilePicUrl?: string;
  assignedHours?: number;
  countOfTaskAssigned?: number;
}

/** Row from GET /projects/:id/members-status */
export interface ProjectMemberStatusInfoDto {
  id?: number;
  name?: string;
  designation?: string;
  image?: string | null;
  thumbnailBase64?: string | null;
  profilePic?: string | null;
  isFallbackAvatar?: boolean;
}

export interface ProjectMemberStatusRowDto {
  memberID?: number;
  MemberInfo?: ProjectMemberStatusInfoDto | null;
  memberInfo?: ProjectMemberStatusInfoDto | null;
  status?: string;
  Status?: string;
  count_of_task_assigned?: number;
  count_of_stages?: number;
  assigned_hours?: number;
}

export interface ProjectMembersStatusApiResponse {
  success?: boolean;
  data?: ProjectMemberStatusRowDto[];
  message?: string;
}

/** Slot under `data.projects[].employees[]` from reporting-manager-by-range (support). */
export interface ReportingManagerProjectSlotDto {
  userId: string;
  name?: string;
  approvalStatus?: string;
  totalLoggedHours?: number;
  distinctTaskCount?: number;
  profilePicUrl?: string;
}

export interface ReportingManagerProjectRowDto {
  projectId: string;
  projectCode: string;
  slots: ReportingManagerProjectSlotDto[];
}

export interface ReportingManagerByRangeParsed {
  employees: ApiEmployee[];
  /** For each reportee, project ids they appear under in `projects` (for pending-approval merge). */
  empToProjectIds: Record<string, string[]>;
  projectRows: ReportingManagerProjectRowDto[];
}

export interface ApiEmployeeListResponse {
  employees: ApiEmployee[];
  empToProjectIds?: Record<string, string[]>;
  reportingManagerProjectRows?: ReportingManagerProjectRowDto[];
}

/** Row from GET /project-timesheet/manager-timesheets-by-range */
export interface ManagerTimesheetSubmitterDto {
  userId?: number;
  user_id?: number;
  name?: string;
  profilePic?: string;
  image?: string;
  thumbnailBase64?: string;
  approvalStatus?: string;
  totalLoggedHours?: number;
  distinctTaskCount?: number;
}

export interface ManagerTimesheetEmployeeRowDto {
  submitter?: ManagerTimesheetSubmitterDto | null;
}

/** API may return `{ success, data }` or a flat payload. */
export interface ManagerTimesheetsByRangeResponse {
  success?: boolean;
  data?: {
    projectId?: number;
    employees?: ManagerTimesheetEmployeeRowDto[];
  };
  projectId?: number;
  employees?: ManagerTimesheetEmployeeRowDto[];
}

export interface ApiTimesheetEntry {
  id: string;
  /**
   * Unique timesheet line id from pending-approval task `detail_id`.
   * Sent on manager-review as `hourEdits.detailId` together with CRM `taskId`.
   */
  timesheetDetailId?: number;
  /** Day header id from pending-approval block `timesheet_id` (sent on manager-review body). */
  timesheetId?: number;
  /** CRM task id from pending-approval task `task_id` (sent on manager-review as `hourEdits.taskId`). */
  taskId?: number;
  empId: string;
  date: string;
  projectId: string;
  projectName: string;
  /** From pending-approval block e.g. `project_code` */
  projectCode?: string;
  /** From pending-approval block e.g. `project_type` (`RA`, `support`, …) */
  projectType?: string;
  projectColor: string;
  /** Phase / stage name from CRM task line. */
  stage: string;
  /** Phase description (shown in brackets beside stage name). */
  stageDescription?: string;
  milestone: string;
  /** Milestone description (shown in brackets beside milestone name). */
  milestoneDescription?: string;
  task: string;
  hours: number;
  isBillable: boolean;
  isAutoTracked: boolean;
  aiConfidenceScore: number | null;
  comment: string;
  /** Hours inferred from desktop / app tracking when `isAutoTracked` is true */
  screenDetectedHours?: number | null;
  /** From RA pending-approval `activity_type` */
  pmActivityType?: 'manual' | 'tracked' | 'untracked';
  pmAppId?: number | null;
  pmAppLabel?: string;
  /** Pending-approval block `status` for this project-day (RM weekly status tabs). */
  blockApprovalStatus?: ApprovalStatus;
}

export function pmActivityKindFromEntry(
  e: Pick<ApiTimesheetEntry, 'pmActivityType' | 'stage'>,
): 'manual' | 'tracked' | 'untracked' {
  if (e.pmActivityType === 'manual' || e.pmActivityType === 'tracked' || e.pmActivityType === 'untracked') {
    return e.pmActivityType;
  }
  const s = (e.stage ?? '').toLowerCase();
  if (s === 'tracked' || (s.includes('tracked') && !s.includes('untrack'))) return 'tracked';
  if (s === 'untracked' || s.includes('untrack')) return 'untracked';
  return 'manual';
}

export interface ApiTimesheetEntriesResponse {
  entries: ApiTimesheetEntry[];
}

/** POST project-timesheet/pending-approval (PM) or support-project-timesheets/pending-approval (Approval / RM). */
export interface PendingApprovalPostBody {
  userId: number;
  startDate: string;
  endDate: string;
  projectId: number;
  /** Omitted for “All”; backend enum: submitted | approved | rejected. */
  status?: 'submitted' | 'approved' | 'rejected';
}

/**
 * Row from GET /project-timesheet/submit-trail?projectId=
 * (submission batch / approval-trail entry; same shape the employee submit-for-approval flow persists).
 */
export interface ProjectTimesheetSubmitTrailRow {
  id: number;
  submissionBatchId: string;
  trailEvent?: string;
  userId: number;
  submitterName?: string;
  projectId: number;
  type: string;
  periodStart: string;
  periodEnd: string;
  approverUserId: number;
  timesheetCount: number;
  workplanTimesheetIds: number[];
  remarkSnapshot: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectTimesheetSubmitTrailResponse {
  success: boolean;
  data: ProjectTimesheetSubmitTrailRow[];
  message: string;
}

/** POST /project-timesheet/approval-status-by-range */
export interface ProjectTimesheetApprovalStatusByRangeBody {
  userId: number;
  projectId: number;
  startDate: string;
  endDate: string;
  approvalStatus: 'approved' | 'rejected';
  remark: string;
}

/** POST /support-project-timesheets/approval-status-by-range — same body as project bulk approval. */
export type SupportProjectTimesheetApprovalStatusByRangeBody =
  ProjectTimesheetApprovalStatusByRangeBody;

/** After mapping POST pending-approval JSON into PM view models. */
export interface PendingApprovalTimesheetMapped {
  entries: ApiTimesheetEntry[];
  dayStatuses: Record<string, ApprovalStatus>;
}

export interface ApiDayStatusResponse {
  statuses: Record<string, ApprovalStatus>;
}

export interface ApiApproveRequest {
  empId: string;
  dateStr: string;
  note: string;
  pmId: string;
}

/** Result of PM approve/reject / bulk approval-status-by-range calls. */
export interface TimesheetApprovalMutationResult {
  success: boolean;
  message: string;
}

export interface ApprovalStatusByRangeApiResponse {
  success?: boolean;
  message?: string;
}

export interface ApiApproveResponse {
  success: boolean;
  updatedStatus: 'approved';
  message?: string;
}

export interface ApiRejectRequest {
  empId: string;
  dateStr: string;
  reason: string;
  pmId: string;
  currentCount: number;
}

export interface ApiRejectResponse {
  success: boolean;
  updatedStatus: 'rejected';
  rejectionCount: number;
  message?: string;
}

export interface ApiEditTimesheetRequest {
  empId: string;
  dates: string[];
  pmId: string;
  entries: ApiTimesheetEntry[];
}

export interface ApiEditTimesheetResponse {
  success: boolean;
  savedEntries: ApiTimesheetEntry[];
}

/** One line in POST .../manager-review `hourEdits`. */
export interface ProjectTimesheetHourEdit {
  /** CRM task id (`task_id`). */
  taskId: number;
  /** Timesheet line id (`detail_id`) — required when backend provides it. */
  detailId?: number;
  loggedHours: number;
}

export interface ProjectTimesheetManagerReviewBody {
  approvalStatus: 'approved' | 'rejected';
  remark: string;
  /** Grouped by pending-approval `timesheet_id`, each with task hour edits. */
  timesheets: Array<{
    timesheetId: number;
    hourEdits: ProjectTimesheetHourEdit[];
  }>;
}

export interface ApiAssignedTask {
  taskId: string;
  task: string;
  projectId: string;
  projectName: string;
  projectColor: string;
  stage: string;
  milestone: string;
  plannedHours: number;
  startDate: string;
  endDate: string;
  isBillable: boolean;
}

export interface ApiAssignedTasksResponse {
  tasks: ApiAssignedTask[];
}

export interface ApiUnreportedApp {
  id: string;
  /** Activity title: API remark when set (except NA/N/A), else taskName, else software name. */
  title: string;
  /** Backend activity app id (for name lookup). */
  appId?: number | null;
  /** Software display name resolved from `appId`. */
  appName: string;
  date: string;
  hours: number;
  timeRange: string;
}

export interface ApiUnreportedAppsResponse {
  apps: ApiUnreportedApp[];
}

export interface ApiHistoryEntry {
  id: string;
  empId: string;
  dateStr: string;
  action: HistoryAction;
  actorName: string;
  actorRole: string;
  timestamp: string;
  note: string;
}

export interface ApiHistoryResponse {
  history: ApiHistoryEntry[];
}

// ═══════════════════════════════════════════════════════════
// UI DOMAIN TYPES
// ═══════════════════════════════════════════════════════════

export interface UiEmployee extends ApiEmployee {
  pendingCount: number;
  approvedCount: number;
  rejectedCount: number;
}

export interface UiTimesheetEntry extends ApiTimesheetEntry {
  _deleted?: boolean;
  _isNew?: boolean;
}

export interface UiAssignedTask extends ApiAssignedTask {
  loggedHours: number;
  status: TaskStatus;
}

export interface DayStatusMap {
  [empId: string]: Record<string, ApprovalStatus>;
}

export interface RejectionMeta {
  count: number;
  note: string;
}

export interface FilterCounts {
  all: number;
  pending: number;
  approved: number;
  rejected: number;
}

export interface WeeklyTaskRow {
  projectId: string;
  taskName: string;
  path: string;
  projColor: string;
  projName: string;
  /** Shown in project chip when set; else UI falls back to `projectId`. */
  projectCode?: string;
  projectType?: string;
  activityKind: 'manual' | 'tracked' | 'untracked';
  appLabel: string;
  entriesByDate: Record<string, UiTimesheetEntry[]>;
  weekTotal: number;
}

export interface WeeklyProjectGroup {
  projectId: string;
  projectName: string;
  projectColor: string;
  projectCode?: string;
  projectType?: string;
  tasks: WeeklyTaskRow[];
  projectWeekTotal: number;
}

export interface ModalApproveData {
  empId: string;
  dateStr: string;
  empName: string;
}

export interface ModalRejectData {
  empId: string;
  dateStr: string;
  empName: string;
  rejCount: number;
  limit: number;
}

export interface ModalEditData {
  /** Route / PM context project id (ensures manager-review URL even if entries omit it). */
  projectId?: string;
  empId: string;
  empName: string;
  dates: string[];
  entries: UiTimesheetEntry[];
}

export interface ModalAssignedData {
  empName: string;
  weekLabel: string;
  tasks: UiAssignedTask[];
}

/** PM bulk Approve all / Reject all — confirmation modal. */
export interface ModalBulkRangeApprovalData {
  mode: 'approve' | 'reject';
  empName: string;
  rangeLabel: string;
  pendingCount: number;
}

export const STAGE_OPTIONS: string[] = [
  'Programming',
  'Concept Design',
  'Schematic Design',
  'Design Development',
  'Construction Documents',
  'Bidding',
  'Construction Administration',
];

export const MILESTONE_OPTIONS: string[] = [
  'Site Analysis',
  'Client Brief',
  'Concept Dev',
  'Massing Studies',
  'Design Coord',
  'Technical Studies',
  'Program Docs',
];

export const TASK_OPTIONS: string[] = [
  'Design Iteration',
  'Site Visit',
  'Zoning Analysis',
  'Requirements Gathering',
  'Stakeholder Interviews',
  'Brief Documentation',
  'Concept Sketches',
  '3D Massing',
  'Coordination Meeting',
  'Environmental Assessment',
  'MEP Coordination',
  'Structural Coordination',
  'Area Schedule',
];

/** Keep in sync with `$rejection-limit` in styles-timesheet-tokens.scss */
export const PM_REJECTION_LIMIT = 2;
